import React, { useEffect } from 'react';
import { X } from 'react-feather';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { RouteNames } from '../../constants';
import styles from './UpgradePopup.module.scss';

/**
 * "Monthly Coupons Exceeded?" upgrade popup — shown when a standard
 * (non-Premium) customer tries to reserve a coupon after reaching their
 * monthly coupon limit (see CouponAction.js `limitReached`).
 *
 * Same shell as the "Log in to continue" (LoginPrompt) and Session popups:
 * soft pink card, red outlined close button, image panel on the left. On
 * narrow screens the image becomes a short banner on top instead of being
 * hidden, since it carries the "Monthly Coupons Exceeded?" headline.
 *
 * "Upgrade" goes to the Premium subscribe page — the same place the
 * membership card's "Get Premium" button goes.
 */

const IMAGE_SRC = `${process.env.PUBLIC_URL}/images/upgrade-popup/monthly-limit.webp`;

// Gold crown above the heading.
const GoldCrown = () => (
  <svg viewBox="0 0 120 84" aria-hidden="true" className={styles.crown}>
    <defs>
      <linearGradient id="upgrade-popup-gold" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#F6D46B" />
        <stop offset="0.45" stopColor="#D9A92A" />
        <stop offset="0.75" stopColor="#F2CF5C" />
        <stop offset="1" stopColor="#B8860B" />
      </linearGradient>
    </defs>
    <path
      fill="url(#upgrade-popup-gold)"
      d="M14 82 4 26c-.4-2.4 2.4-4 4.3-2.4L36 46 57.4 3.6c1.1-2.2 4.1-2.2 5.2 0L84 46l27.7-22.4c1.9-1.6 4.7 0 4.3 2.4L106 82Z"
    />
  </svg>
);

// Small white crown inside the Upgrade button.
const ButtonCrown = () => (
  <svg viewBox="0 0 24 18" aria-hidden="true" className={styles.btnCrown}>
    <path fill="currentColor" d="M3 17 1 5.6l5.6 4.6L12 1l5.4 9.2L23 5.6 21 17Z" />
  </svg>
);

const UpgradePopup = ({ isOpen, onClose }) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = e => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className={styles.overlay}
      role="presentation"
      onClick={e => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgrade-popup-title"
        onClick={e => e.stopPropagation()}
      >
        <button type="button" aria-label="Close" className={styles.close} onClick={onClose}>
          <X size={30} />
        </button>

        <div className={styles.imageCol}>
          <img src={IMAGE_SRC} alt="Monthly coupons exceeded?" className={styles.image} />
        </div>

        <div className={styles.contentCol}>
          <GoldCrown />
          <h2 id="upgrade-popup-title" className={styles.title}>
            <span className={styles.titleSmall}>Upgrade to</span>
            <span className={styles.titleBig}>Premium</span>
          </h2>
          <p className={styles.desc}>Unlock more offers, more coupons, and more reasons to smile with Premium.</p>
          <button
            type="button"
            className={styles.upgradeBtn}
            onClick={() => {
              onClose();
              navigate(RouteNames.PAYMENT_FORM);
            }}
          >
            <ButtonCrown />
            Upgrade
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default UpgradePopup;
